import React, { useEffect, useState } from 'react';
import { getBackendOrigin } from '@/lib/apiBase';
import { cn } from '@/lib/utils';

const BACKEND_ORIGIN = getBackendOrigin();

export function resolveProductImageUrl(image) {
  if (!image) return '';
  const sourceUrl = (image.source_url || '').trim();
  const localUrl = (image.url || '').trim();

  if (localUrl.startsWith('http')) {
    return localUrl;
  }

  if (localUrl && image.downloaded !== false) {
    return `${BACKEND_ORIGIN}${localUrl}`;
  }

  if (sourceUrl) {
    return sourceUrl;
  }

  if (localUrl) {
    return `${BACKEND_ORIGIN}${localUrl}`;
  }

  return '';
}

function withCacheBust(url, version) {
  if (!url || version <= 0) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${version}`;
}

export default function ProductImageView({
  image,
  alt,
  className,
  style,
  onClick,
  onError: onErrorProp,
  imageVersion = 0,
  loading = 'eager',
  title,
}) {
  const [src, setSrc] = useState(() => resolveProductImageUrl(image));
  const [usedFallback, setUsedFallback] = useState(false);

  useEffect(() => {
    setSrc(resolveProductImageUrl(image));
    setUsedFallback(false);
  }, [image?.url, image?.source_url, image?.filename, image?.downloaded, imageVersion]);

  const handleError = () => {
    const fallback = (image?.source_url || '').trim();
    if (fallback && !usedFallback && src !== fallback) {
      setUsedFallback(true);
      setSrc(fallback);
      return;
    }
    onErrorProp?.();
  };

  const displaySrc = withCacheBust(src, imageVersion);

  return (
    <img
      src={displaySrc}
      alt={alt}
      title={title}
      loading={loading}
      decoding="async"
      onClick={onClick}
      onError={handleError}
      className={className}
      style={style}
    />
  );
}

export function ProductImageThumb({
  image,
  alt,
  selected,
  onSelect,
  imageVersion,
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-16 h-16 rounded-lg border-2 overflow-hidden flex items-center justify-center bg-gray-50 shrink-0 p-1',
        selected
          ? 'border-blue-500 ring-1 ring-blue-200'
          : 'border-gray-200 hover:border-gray-300'
      )}
    >
      <ProductImageView
        image={image}
        alt={alt}
        imageVersion={imageVersion}
        className="w-full h-full object-contain"
      />
    </button>
  );
}
